import { formatDate } from '@angular/common';
import { AfterContentChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Subject } from 'rxjs';
import { CouponCodeService } from 'src/app/_business-dashboard/_services/bizz-coupon-codes.services';
import { SalesChannelService } from 'src/app/_business-dashboard/_services/bizz-sales-channel.services';
import { AllowedDaysList, ChannelList, CustomerTypeList, DiscountTypeList, Status, ValidityList } from 'src/app/_models/coupon-management';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { GlobalService } from 'src/app/_services/global.service';
import { SubscriptionPlanService } from 'src/app/_services/subscription-plan.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { environment } from 'src/environments/environment';
import { subscriptionDiscountValidator } from 'src/app/validators/subscription-discount.validator';


@Component({
  selector: 'app-bizz-coupon-codes-form',
  templateUrl: './bizz-coupon-codes-form.component.html',
  styleUrls: ['./bizz-coupon-codes-form.component.scss']
})
export class BizzCouponCodesFormComponent implements OnInit {
  _global = GlobalService
  destroy$: Subject<boolean> = new Subject<boolean>()

  editform: FormGroup

  // input Field Description
  maxDiscountDesc = `Please enter "0" to remove the Max discount restriction.`
  maxCouponUseDesc = `Set usage limit (e.g., 100 for first 100 customers). Leave it blank for unlimited use.`


  couponData;
  allowedCouponsDummy = []
  // Bind the dropdown values to the form from the service model 
  salesChannelList: any[] = [];
  statusList: any[] = Status;
  customerTypeList = CustomerTypeList;
  discountTypeList = DiscountTypeList;
  validityList = ValidityList;
  allowedDaysList = AllowedDaysList;
  allowedLocationsList: any[] = [];
  selectedSubscriptionPlanList: any[] = []

  maxValue: number;
  subscriptionPlanDropdownIcon: string = 'pi pi-chevron-down';
  selectedAppNotificationText: any;
  genratedLink: any;


  @Input() isNew: boolean = true
  @Input() isViewOnly: boolean
  @Output() onSubmit = new EventEmitter();

  subscriptionValidityList = [
    { id: 1, viewValue: 'Once' },
    { id: 2, viewValue: 'Multiple months' },
    { id: 3, viewValue: 'Forever' },
  ];

  billingIntervalList = [
    { id: 1, viewValue: 'Monthly' },
    { id: 2, viewValue: 'Yearly' },
  ];

  isStripeCouponCreated: boolean = false;
  errorMessage: string = "";
  private readonly forceStripeProdKeys = false;
  constructor(
    private _fb: FormBuilder,
    public _couponCodeService: CouponCodeService,
    private _spinner: NgxSpinnerService,
    private _connMessageService: ConnMessageService,
    public _businessSetupService: BusinessSetupService,
    public _subscriptionPlanService: SubscriptionPlanService,
    private _salesChannelService: SalesChannelService,
    public _abd: AppBaseDataService,
    private clipboard: Clipboard,
  ) { }


  ngOnInit(): void {
    const url = this.isNew ? "https://app.lincsell.com" : this._couponCodeService.activeItem?.printText;
    const urlUpdate = !this.isNew
      ? url?.split('trackingId=')[0].slice(0, -1)
      : this.isNew ? "https://app.lincsell.com" : this._global.whiteLabel.siteURL;

    const { start, end } = this.getDefaultValidityDates();

    this.couponData = {
      discountName: this._couponCodeService.activeItem?.discountName,
      customerType: this._couponCodeService.activeItem?.customerType,
      discountValueType: this._couponCodeService.activeItem?.discountValueType,
      discountValue: this._couponCodeService.activeItem?.discountValue,
      maximumAllowed: this._couponCodeService.activeItem?.maximumAllowed,
      maxCouponUse: this._couponCodeService.activeItem?.maxCouponUse,
      validity: this._couponCodeService.activeItem?.validity,
      validityStartDate: start,
      validityEndDate: end,
      allowedDaysList: this._couponCodeService.activeItem?.allowedDaysList,
      allowedLocationsList: this._couponCodeService.activeItem?.allowedLocationsList, // may be ids or cosmosIds
      isActive: this._couponCodeService.activeItem?.isActive === true ? 1 : 0,
      couponCodesList: this._couponCodeService.activeItem?.couponCodesList,
      printText: urlUpdate,
      discountedPrice: this._couponCodeService.activeItem?.discountedPrice || '',
      subscriptionValidity: this._couponCodeService.activeItem?.subscriptionValidity || 1,
      subscriptionDiscountMoths: this._couponCodeService.activeItem?.subscriptionDiscountMoths || '',
      subscriptionDiscountInterval: this._couponCodeService.activeItem?.subscriptionDiscountInterval || 1,
      salesChannelId: this._couponCodeService.activeItem?.salesChannel?.id
    };

    const stripeId = this._couponCodeService.activeItem?.stripeCouponId;

    this.isStripeCouponCreated = !!stripeId && !this.isNew;
    this.loadCouponFormData();
  }

  startCreateNewCoupon(): void {
    this._couponCodeService.blankActiveItem();
    this._couponCodeService.showEditCoupon = false;
    this._couponCodeService.showNewEditCoupon = true;
  }

  private loadCouponFormData(): void {
    this._abd.showSpinner();

    forkJoin({
      channels: this._salesChannelService.getAllSalesChannel(),
      plans: this._subscriptionPlanService.getSubscriptionPlanListV2()
    }).subscribe({
      next: ({ channels, plans }) => {
        // 1) Build dropdown sources (no form patching yet)
        if (channels?.success) {
          const list = channels.data || [];
          this.salesChannelList = list.map((v: any) => ({
            id: Number(v.id),
            viewValue: v.fullName
          }));
        }

        if (plans?.success) {
          const list = plans.data || [];
          this.allowedLocationsList = list.filter(f => f.isActive && f.price > 0)
            .map((v: any) => ({
              id: Number(v.id),
              cosmosId: String(v.cosmosId),
              viewValue: v.title,
              planId: v.planId,
              productType: v.productType,
              stripeProductKey: this.resolveStripeProductKey(v),
              price: v.price,
              monthlyPrice: v.monthlyPrice,
              yearlyPrice: v.yearlyPrice
            }))
            .filter((x: any) => x.cosmosId !== '162851695001' && x.cosmosId !== '1628516995005');
        }

        // 2) Init form ONCE
        this.initForm();
        this.setCustomValidator();

        // 3) Preselect values for Edit mode (if any)
        const matchedChannel =
          this.couponData.salesChannelId != null
            ? this.salesChannelList.find(x => x.id === Number(this.couponData.salesChannelId)) || null
            : null;

        const allowed = this.couponData.allowedLocationsList;
        const matchedPlan =
          Array.isArray(allowed) && allowed.length
            ? this.allowedLocationsList.find((p: any) =>
              allowed.includes(String(p.cosmosId)) ||
              allowed.includes(p.cosmosId) ||
              allowed.includes(String(p.id)) ||
              allowed.includes(p.id)
            ) || null
            : null;

        // 4) Patch selections safely (once)
        this.f.appNotificationText.setValue(matchedChannel);
        this.f.appNotificationText.updateValueAndValidity();

        this.f.allowedLocationsList.setValue(matchedPlan);
        this.f.allowedLocationsList.updateValueAndValidity();

        this._abd.hideSpinner();
      },
      error: () => {
        this._abd.hideSpinner();
        this._connMessageService.showToastMessage('Failed to load required data.', 'error');
        // still init to keep UI responsive
        this.initForm();
        this.setCustomValidator();
      }
    });
  }

  getDefaultValidityDates(): { start: Date; end: Date } {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 30); // default: +30 days

    const isValidDate = (d: any) =>
      d &&
      d !== "0001-01-01T00:00:00" &&
      !isNaN(new Date(d).getTime()) &&
      new Date(d).getFullYear() > 1900; // extra safety

    const start = isValidDate(this.couponData?.validityStartDate)
      ? new Date(this.couponData.validityStartDate)
      : startDate;

    const end = isValidDate(this.couponData?.validityEndDate)
      ? new Date(this.couponData.validityEndDate)
      : endDate;

    return { start, end };
  }


  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
  copy(text: any) {
    this.clipboard.copy(text);
    this._connMessageService.showToastMessage('copied successfully', 'success');
  }

  getSalesChannel() {
    this._abd.showSpinner()
    this._salesChannelService.getAllSalesChannel().subscribe(res => {

      this.initForm();
      this.setCustomValidator()
      if (res.success) {
        this._abd.hideSpinner()
        const salesChannelList = res.data
        this.salesChannelList = salesChannelList.map((v) => {
          return {
            id: +v.id,
            viewValue: v.fullName
          }

        })
        let value = this._couponCodeService.activeItem?.salesChannel?.id
        if (value !== undefined) {
          this.selectedAppNotificationText = this.salesChannelList.filter((f) => f.id === +value)[0]
        }
        this.f.appNotificationText.setValue(this.selectedAppNotificationText);
        this.f.appNotificationText.updateValueAndValidity()
      }
    }, (error) => {
      //  console.log(error)
      this.initForm();
      this.setCustomValidator()
    })
  }

  getSubscriptionPlans() {
    this.subscriptionPlanDropdownIcon = 'pi pi-spin pi-spinner';
    this._subscriptionPlanService.getSubscriptionPlanListV2().subscribe({
      next: (res) => {
        this.subscriptionPlanDropdownIcon = 'pi pi-chevron-down';

        if (res.success) {
          const subscriptionPlans = res.data;
          this.allowedLocationsList = subscriptionPlans.filter(f => f.isActive && f.price > 0)
            .map((v) => ({
              id: +v.id,
              cosmosId: v.cosmosId,
              viewValue: v.title,
              planId: v.planId,
              productType: v.productType,
              stripeProductKey: this.resolveStripeProductKey(v),
              price: v.price,
              monthlyPrice: v.monthlyPrice,
              yearlyPrice: v.yearlyPrice
            }))
            .filter((x) => x.cosmosId !== 162851695001 && x.cosmosId !== 1628516995005);

          // ✅ Update value AFTER plans are loaded
          let matchedPlan = null;

          if (!this.isNew && this.couponData.allowedLocationsList?.length) {
            matchedPlan = this.allowedLocationsList.find((plan) =>
              this.couponData.allowedLocationsList.includes(String(plan.cosmosId)) ||
              this.couponData.allowedLocationsList.includes(plan.cosmosId)
            );
          }

          // ✅ Set the form value safely
          setTimeout(() => {
            this.f.allowedLocationsList.setValue(matchedPlan || null);
            this.f.allowedLocationsList.updateValueAndValidity();
          }, 0);
        }
      },
      error: () => {
        this.subscriptionPlanDropdownIcon = 'pi pi-chevron-down';
        this._connMessageService.showToastMessage('Failed to load subscription plans.', 'error');
      },
    });
  }


  get f() {
    return this.editform.controls;
  }

  private resolveStripeProductKey(item: any): string {
    if (!item) return '';
    const sandboxKey = item.StripeSandboxProductKey || item.stripeSandboxProductKey || '';
    const prodKey = item.StripeProductKey || item.stripeProductKey || '';
    const useProdKeys = environment.production || this.forceStripeProdKeys;
    const selectedKey = useProdKeys ? (prodKey || sandboxKey) : (sandboxKey || prodKey);
    return Number.isNaN(Number(selectedKey)) ? selectedKey : '';
  }

  initForm() {
    const discountType = this.couponData?.discountValueType || 0;
    // ✅ Choose default URL based on discount type
    const defaultUrl =
      discountType === 3
        ? 'https://app.lincsell.com'
        : 'https://app.lincsell.com';

    let allowedDaysDataHolder = []
    if (this._couponCodeService?.activeItem) {
      const allowedDays = this._couponCodeService.activeItem.allowedDaysList;

      if (!allowedDays || allowedDays.length === 0) {
        // ✅ Default to weekdays only (Mon–Fri)
        const weekdayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
        allowedDaysDataHolder = this.allowedDaysList.filter(day =>
          weekdayNames.includes(day.viewValue)
        );
      } else {
        // ✅ Use only the selected days
        allowedDays.forEach(y => {
          const matched = this.allowedDaysList.find(x => x.id === y);
          if (matched) allowedDaysDataHolder.push(matched);
        });
      }
    }
    const { start, end } = this.getDefaultValidityDates();

    this.editform = this._fb.group({
      discountName: [{ value: this.couponData.discountName, disabled: this.isViewOnly }, [Validators.required, Validators.maxLength(100)]],
      // appNotificationText: [{value:this.selectedAppNotificationText, disabled: this.isViewOnly}, Validators.required],
      appNotificationText: [{ value: this.salesChannelList.filter(x => x.id == this.couponData.appNotificationText)[0], disabled: this.isViewOnly }],
      customerType: [4],
      // discountValueType: [{ value: this.discountTypeList.filter(x => x.id == 3)[0], disabled: this.isViewOnly }, Validators.required],
      discountValueType: [{ value: { id: 3, viewValue: 'Subscription' }, disabled: true }, Validators.required],

      discountValue: [{ value: this.couponData.discountValue, disabled: this.isViewOnly }, Validators.pattern(/^([0-9]\d*)(\.\d+)?$/)],
      maximumAllowed: [{ value: this.couponData.maximumAllowed, disabled: this.isViewOnly }, [Validators.pattern(/^([0-9]\d*)(\.\d+)?$/)]],
      maxCouponUse: [{ value: this.couponData.maxCouponUse, disabled: this.isViewOnly }, [Validators.max(999), Validators.pattern(/^([0-9]\d*)(\.\d+)?$/)]],
      validity: [{ value: this.validityList.filter(x => x.id == this.couponData.validity)[0], disabled: this.isViewOnly }],
      validityDate: [{ value: [start, end], disabled: this.isViewOnly }],
      allowedDaysList: [{ value: allowedDaysDataHolder, disabled: this.isViewOnly }],
      // allowedLocationsList: [{ value: this.selectedSubscriptionPlanList, disabled: this.isViewOnly }],

      isActive: [{ value: this.statusList.filter(x => x.id == this.couponData.isActive)[0], disabled: this.isViewOnly }],
      couponCodesList: [{ value: this.couponData.couponCodesList, disabled: this.isViewOnly }, Validators.required],
      printText: [{ value: this.couponData.printText || defaultUrl, disabled: this.isViewOnly }, Validators.required],
      discountedPrice: [{ value: this._couponCodeService.activeItem?.discountedPrice || '', disabled: this.isViewOnly },
      [Validators.required, Validators.min(0.01), subscriptionDiscountValidator()]
      ],

      subscriptionValidity: [
        {
          value: this.isNew
            ? this.subscriptionValidityList[0]
            : this.subscriptionValidityList.find(x => x.id == this.couponData.subscriptionValidity) || this.subscriptionValidityList[0],
          disabled: this.isViewOnly
        },
        Validators.required
      ],
      monthsCount: [
        { value: this.couponData.subscriptionDiscountMoths || null, disabled: this.isViewOnly },
        [
          Validators.min(1),         // must be >= 1
          Validators.pattern(/^\d*$/) // allow only digits (optional)
        ]
      ],
      billingInterval: [
        {
          value: this.isNew
            ? this.billingIntervalList[0]
            : this.billingIntervalList.find(x => x.id == this.couponData.subscriptionDiscountInterval) || this.billingIntervalList[0],
          disabled: this.isViewOnly
        },
        Validators.required
      ],
      allowedLocationsList: [
        {
          value: this.isNew
            ? null
            : this.allowedLocationsList.filter((x) =>
              (this.couponData.allowedLocationsList || []).includes(String(x.id))
            ),
          disabled: this.isViewOnly
        },
        Validators.required // ✅ required validator here
      ]
    })

    this.f.discountValueType.valueChanges.subscribe((val) => {
      if (!this.isViewOnly) {
        if (val?.id === 3) {
          this.f.printText.setValue('https://app.lincsell.com');
        } else {
          this.f.printText.setValue('https://app.lincsell.com');
        }
      }
    });




    // 🧩 Watch for Validity = Multiple Months
    this.editform.get('subscriptionValidity')?.valueChanges.subscribe((validity) => {
      if (validity?.id === 2 && this.f.discountValueType?.value?.id === 3) {
        this.editform.get('monthsCount')?.setValidators([
          Validators.required,
          Validators.pattern(/^\d+$/),
        ]);
      } else {
        this.editform.get('monthsCount')?.clearValidators();
      }
      this.editform.get('monthsCount')?.updateValueAndValidity();
    });

    this.setSubscriptionValidators(true);
    this.f.allowedLocationsList.markAsTouched({ onlySelf: true }); // shows red border
    this.f.allowedLocationsList.markAsUntouched(); // hides error text
    this.f.allowedLocationsList.valueChanges.subscribe(() => {
      this.f.discountedPrice.updateValueAndValidity();
    });

  }



  setCustomValidator() {

    this.validation(this.f.discountValueType.value.id)
    this.f.discountValueType.valueChanges.subscribe(val => {
      this.validation(val.id)
    })

    this.f.validity.valueChanges.subscribe(discountValue => {
      if (discountValue.viewValue == 'Valid between date range') {
        this.f.validityDate.setValidators([Validators.required]);
      } else {
        this.f.validityDate.clearAsyncValidators();
      }
      this.f.validityDate.updateValueAndValidity();
    })
    // 🔹 Value change watchers
    this.f.couponCodesList.valueChanges.subscribe(() => {
      this.handleDefaultUrl();
      this.getURL();
    });
    this.f.printText.valueChanges.subscribe(() => this.getURL());
    this.f.allowedLocationsList.valueChanges.subscribe(() => {
      this.handleDefaultUrl();
      this.f.discountedPrice.updateValueAndValidity();
      this.getURL();
    });
    this.f.billingInterval.valueChanges.subscribe(() => {
      this.handleDefaultUrl();
      this.getURL();
    });

    // run once on init
    this.handleDefaultUrl();
    this.getURL();
  }

  handleDefaultUrl() {
    const discountType = this.f.discountValueType?.value?.id;
    const url = this.f.printText.value?.trim() || '';

    // ✅ If URL is empty, add default based on discount type
    if (!url) {
      const defaultUrl =
        discountType === 3
          ? 'https://app.lincsell.com'
          : 'https://app.lincsell.com';
      this.f.printText.setValue(defaultUrl, { emitEvent: false });
    }
  }

  getURL() {
    // this.f.printText.value?.trim()
    let url = environment.production ? 'https://app.lincsell.com' : 'https://preapp.lincsell.com';
    const code = this.f.couponCodesList.value || '';
    const discountType = this.f.discountValueType?.value?.id;
    let finalUrl = '';

    // ✅ 1️⃣ Normalize URL — remove trailing slashes & duplicate ones before hash
    url = url
      .replace(/\/{2,}/g, '/')      // collapse multiple slashes
      .replace('https:/', 'https://') // fix scheme slashes
      .replace('http:/', 'http://')   // fix scheme slashes
      .replace(/\/#/, '#')            // ensure single slash before hash
      .replace(/#\/signup.*$/i, '');  // remove any previous signup part

    // ✅ 2️⃣ Make sure it ends correctly before appending
    if (!url.endsWith('/')) url += '/';

    if (discountType === 3) {
      const selectedPlan = this.f.allowedLocationsList?.value;
      const selectedInterval = this.f.billingInterval?.value;

      const planId = selectedPlan?.planId || selectedPlan?.id || '0';
      const intervalValue = selectedInterval?.viewValue?.toLowerCase() || '';
      const intervalName =
        intervalValue.includes('month') ? 'm' :
          intervalValue.includes('year') ? 'y' : 'm';

      // ✅ 3️⃣ Build the final signup URL safely
      finalUrl = `${url}#/signup?selectedplan=${planId}&interval=${intervalName}&trackingId=${code}`;
    } else {
      finalUrl = url.includes('?')
        ? `${url}&trackingId=${code}`
        : `${url}?trackingId=${code}`;
    }

    this.genratedLink = finalUrl;
  }


  validation(val?: any) {
    if (val === 0) {
      // Flat
      this.maxValue = 999999;
      this.f.discountValue.setValidators([Validators.required, Validators.min(0), Validators.max(this.maxValue), Validators.pattern(/^([0-9]\d*)(\.\d+)?$/), this.maxFractionalDigitsValidator(2)]);
      this.f.maximumAllowed.clearValidators();
    } else if (val === 1) {
      // Percent
      this.maxValue = 100;
      this.f.discountValue.setValidators([Validators.required, Validators.min(0), Validators.max(this.maxValue), Validators.pattern(/^([0-9]\d*)(\.\d+)?$/), this.maxFractionalDigitsValidator(2)]);
      this.f.maximumAllowed.setValidators([Validators.required, Validators.min(0), Validators.max(999999), Validators.pattern(/^([0-9]\d*)(\.\d+)?$/), this.maxFractionalDigitsValidator(2)]);
    } else {
      // Tracking only
      this.maxValue = 0;
      this.f.discountValue.clearValidators();
      this.f.maximumAllowed.clearValidators();
      this.f.maxCouponUse.clearValidators();
      this.f.validity.clearValidators();
      this.f.validityDate.clearValidators();
      this.f.allowedDaysList.clearValidators();
      // this.f.allowedLocationsList.clearValidators();
    }
    this.f.discountValue.updateValueAndValidity();
    this.f.maximumAllowed.updateValueAndValidity();
    this.f.maxCouponUse.updateValueAndValidity();
    this.f.validity.updateValueAndValidity();
    this.f.validityDate.updateValueAndValidity();
    this.f.allowedDaysList.updateValueAndValidity();
    this.f.allowedLocationsList.setValue(this.selectedSubscriptionPlanList);
    this.f.allowedLocationsList.updateValueAndValidity();
  }
  maxFractionalDigitsValidator(maxDigits: number) {
    return (control: FormControl) => {
      if (!control.value || isNaN(control.value)) {
        return null;
      }

      const valueString = control.value.toString();
      const decimalIndex = valueString.indexOf('.');
      if (decimalIndex !== -1 && valueString.length - decimalIndex - 1 > maxDigits) {
        return { maxFractionalDigits: true }; // Validation failed
      }
      return null; // Validation passed
    };
  }

  saveFn() {
    try {
      this._abd.showSpinner();

      const form = this.f;
      const couponLink = this.genratedLink;

      let allowedLocationsList: number[] = [];
      const allowedLocationsValue = form.allowedLocationsList.value;
      const discountPrice = form.allowedLocationsList.value;
      let subscriptinDiscountType = null;
      let stripeProductKey = null;

      let subscriptionDiscountType: 'monthly' | 'yearly' | null = null;

      const discountedPrice = Number(form.discountedPrice.value);
      const monthlyPrice = Number(discountPrice?.monthlyPrice) || 0;
      const yearlyPrice = Number(discountPrice?.yearlyPrice) || 0;

      // 1️⃣ Validate discounted price
      if (!discountedPrice || discountedPrice <= 0) {
        this._connMessageService.showToastMessage(
          'Discounted price must be a number greater than zero.',
          'error'
        );
        this._abd.hideSpinner();
        return;
      }

      // 2️⃣ Determine applicable subscription type
      if (monthlyPrice && discountedPrice < monthlyPrice) {
        subscriptionDiscountType = 'monthly';
      } else if (yearlyPrice && discountedPrice < yearlyPrice) {
        subscriptionDiscountType = 'yearly';
      } else {
        this._connMessageService.showToastMessage(
          'Discounted price cannot exceed or same to subscription price.',
          'error'
        );
        this._abd.hideSpinner();
        return;
      }



      if (form.monthsCount.value <= 0 && form.subscriptionValidity.value?.id === 2) {
        this._connMessageService.showToastMessage('Months interval must be greater than zero.', 'error');
        this._abd.hideSpinner();
        return;
      }


      if (Array.isArray(allowedLocationsValue)) {
        allowedLocationsList = allowedLocationsValue.map((item: any) => item.id);
        subscriptinDiscountType = allowedLocationsValue[0]?.productType;
        stripeProductKey = allowedLocationsValue[0]?.stripeProductKey;
      } else if (allowedLocationsValue?.id) {
        allowedLocationsList = [allowedLocationsValue.id];
        subscriptinDiscountType = allowedLocationsValue.productType;
        stripeProductKey = allowedLocationsValue.stripeProductKey;
      }

      const formData: any = {
        discountName: form.discountName.value,
        // appNotificationText: form.appNotificationText.value?.id,
        customerType: 4,
        discountValueType: form.discountValueType.value?.id,
        maxCouponUse: form.maxCouponUse.value || 0,
        validity: form.validity.value?.id,
        allowedDaysList: form.allowedDaysList.value?.map((item: any) => item.id) || [],
        allowedLocationsList: allowedLocationsList,
        isActive: form.isActive.value?.id,
        couponCodesList: form.couponCodesList.value,
        PrintText: couponLink,
        discountedPrice: form.discountedPrice.value || 0,
        subscriptionValidity: form.subscriptionValidity.value?.id,
        subscriptionDiscountMoths: form.monthsCount.value || 0,
        subscriptionDiscountInterval: form.billingInterval.value?.id,
        subscriptionDiscountCategory: subscriptinDiscountType || null,
        stripeProductKey: stripeProductKey || null,
      };

      // Handle discount types
      switch (formData.discountValueType) {
        case 0: // Flat
          formData.discountValue = form.discountValue.value || 0;
          break;
        case 1: // Percentage
          formData.discountValue = form.discountValue.value || 0;
          formData.maximumAllowed = form.maximumAllowed.value || 0;
          break;
        // default: // Tracking Only
        //   Object.assign(formData, {
        //     discountValue: 0,
        //     maxCouponUse: 0,
        //     validity: 0,
        //     allowedDaysList: [0, 1, 2, 3, 4, 5, 6],
        //     allowedLocationsList: this.allowedLocationsList.map((item: any) => item.id),
        //   });
        //   break;
      }

      // Handle validity date range
      if (formData.validity === 1) {
        const [start, end] = form.validityDate.value || [];
        formData.validityStartDate = formatDate(start || new Date(), 'MM/dd/yyyy', 'en-US');
        formData.validityEndDate = formatDate(end || '2050-01-01', 'MM/dd/yyyy', 'en-US');
      }

      // Helper to handle response
      const handleResponse = (res: any, successMsg: string, errorMsg: string) => {

        if (res.success) {

          const isCreated = !!this.isStripeCouponCreated;
          (
            !isCreated
              ? this._couponCodeService.addCouponToStripe(formData)
              : this._couponCodeService.updateCouponToStripe(
                formData,
                this._couponCodeService.activeItem?.id
              )
          ).subscribe({
            next: (res) => {
              this.cancelButtonClick();

              this.onSubmit.emit({
                id: res?.data?.id,
                discountName: form.discountName.value
              });

              this._connMessageService.showToastMessage(successMsg, 'success');
            },
            error: (err) => {
              this._abd.hideSpinner();
              this._connMessageService.showToastMessage('Something went wrong', 'error');
              console.error(err);
            }
          });



        } else {
          this._abd.hideSpinner();
          this._connMessageService.showToastMessage(errorMsg, 'error');
        }
      };

      // Save or Update
      const request$ = this.isNew
        ? this._couponCodeService.addCouponV2(formData)
        : this._couponCodeService.updateCouponV2(this._couponCodeService.activeItem?.id, formData);

      request$.subscribe({
        next: (res) => handleResponse(
          res,
          this.isNew ? 'Coupon added successfully' : 'Coupon updated successfully',
          this.isNew ? 'Coupon could not be added.' : 'Coupon could not be updated.'
        ),
        error: (err) => {
          //   console.error('Error saving coupon:', err);
          //   this._abd.hideSpinner();
          this.errorMessage = err?.error?.message || 'An error occurred while saving the coupon.';
          // this._connMessageService.showToastMessage('An error occurred while saving the coupon.', 'error');
        },
      });
    } catch (error) {
      this._abd.hideSpinner();
      console.error('Unexpected error in saveFn:', error);
      this.errorMessage = error?.error?.message || 'Unexpected error occurred. Please try again.';
      this._connMessageService.showToastMessage('Unexpected error occurred. Please try again.', 'error');
    }
  }


  cancelButtonClick() {
    this._couponCodeService.showEditCoupon = false;
    this._couponCodeService.showNewEditCoupon = false;
    this.onSubmit.emit(null); // or a separate event like onCancel
  }
  validateKey(e: any) {
    let specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
    if (this.allowedCouponsDummy.includes(e.value) || specialCharRegex.test(e.value) || e.value.length > 10 || e.value.length < 4) {
      this.f.couponCodesList.setValue([])
    }
    else {
      this.f.couponCodesList.setValue(e.map((item) => item.toUpperCase()))
    }
  }

  getFormErrors(formGroup: FormGroup): any {
    const errors: any = {};
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        const groupErrors = this.getFormErrors(control);
        if (Object.keys(groupErrors).length > 0) {
          errors[key] = groupErrors;
        }
      } else if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }

  setSubscriptionValidators(isRequired: boolean) {
    const textFields = ['discountName', 'allowedLocationsList', 'billingInterval', 'subscriptionValidity'];
    const numberFields = ['discountedPrice'];

    textFields.forEach((field) => {
      const control = this.editform.get(field);
      if (!control) return;

      if (isRequired) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
      }

      control.updateValueAndValidity({ emitEvent: false });
    });

    numberFields.forEach((field) => {
      const control = this.editform.get(field);
      if (!control) return;

      if (isRequired) {
        control.setValidators([
          Validators.required,
          Validators.min(0.01),
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
          subscriptionDiscountValidator()   // ⬅️ THIS LINE IS MANDATORY
        ]);
      } else {
        control.clearValidators();
      }

      control.updateValueAndValidity({ emitEvent: false });
    });
  }


} 
